/**
 * The wire shapes `Rws.AiInsights.Host.Preferences.PreferencesEndpoints` returns: the signed-in person's own
 * application preferences. A person reads and saves only their own; a preference they have never saved comes
 * back at its default.
 */
import { apiFetch } from "./apiFetch";

/** The signed-in person's application preferences. */
export interface PersonPreferences {
  /** Whether the beige provider-warning notices are shown at the top of pages. Shown by default. */
  showProviderWarnings: boolean;
  /** How many recent complete days the projected spend is estimated from: one of `projectionLookbackOptions`. */
  projectionLookbackDays: number;
  /** Whether the signed-in pages show the moving network behind them. Shown by default. */
  animatedBackground: boolean;
}

/** The look-back lengths, in days, a person may choose for the projected spend. Matches the server's list. */
export const projectionLookbackOptions: readonly number[] = [7, 14, 31, 90];

/** The outcome of reading or saving preferences. */
export type PreferencesResult =
  | { kind: "ok"; preferences: PersonPreferences }
  | { kind: "error"; message: string };

const preferencesPath = "/api/preferences/me";

async function preferencesRequest(init: RequestInit): Promise<PreferencesResult> {
  try {
    const response = await apiFetch(preferencesPath, {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}) },
      ...init,
    });
    if (!response.ok) {
      return { kind: "error", message: `The preferences endpoint returned ${String(response.status)}.` };
    }
    return { kind: "ok", preferences: (await response.json()) as PersonPreferences };
  } catch {
    return { kind: "error", message: "The preferences endpoint could not be reached." };
  }
}

/** Calls `GET /api/preferences/me`. */
export async function fetchMyPreferences(): Promise<PreferencesResult> {
  return preferencesRequest({ method: "GET" });
}

/** Calls `PUT /api/preferences/me`, saving every preference, and returns them as saved. */
export async function saveMyPreferences(preferences: PersonPreferences): Promise<PreferencesResult> {
  return preferencesRequest({ method: "PUT", body: JSON.stringify(preferences) });
}
